/**
 * HACCP packing product-temperature checks — category eligibility.
 *
 * Authoritative London Smoke & Cure SKU families:
 *   FS… → sausage
 *   SA… → salmon
 * Plus catalogue category / name cues and sausage-not-bacon overrides.
 */

import { isSausageNotBaconOverrideSku } from './sausageNotBaconSkuOverrides.js';
import { isSalmonBrowseCategory } from './browseCategoryUtils.js';

/** @typedef {'salmon' | 'sausage'} PackingTempCategoryKey */

export const PACKING_TEMP_CATEGORY_KEYS = Object.freeze(['salmon', 'sausage']);

export const PACKING_TEMP_CATEGORY_LABELS = Object.freeze({
    salmon: 'Salmon',
    sausage: 'Sausage',
});

/**
 * Normalize a catalogue category / SKU into a packing-temp category key, or null.
 * @param {string|null|undefined} categoryValue
 * @param {string|null|undefined} sku
 * @returns {PackingTempCategoryKey|null}
 */
export function resolvePackingTempCategoryKey(categoryValue, sku) {
    if (sku && isSausageNotBaconOverrideSku(sku)) return 'sausage';

    // SKU family prefixes — authoritative (do not rely on catalogue category alone)
    if (sku) {
        const upper = String(sku).trim().toUpperCase();
        if (upper.startsWith('FS')) return 'sausage';
        if (upper.startsWith('SA')) return 'salmon';
    }

    const value = (categoryValue || '').toString().toLowerCase();

    if (value.includes('salmon') || value.includes('gravlax') || value.includes('lox')) return 'salmon';
    if (value.includes('sausage')) return 'sausage';
    if (isSalmonBrowseCategory(categoryValue)) return 'salmon';

    if (sku) {
        const upper = String(sku).trim().toUpperCase();
        if (upper.includes('SALM')) return 'salmon';
        if (upper.includes('SAUS') || upper.startsWith('SS-')) return 'sausage';
    }

    return null;
}

/**
 * @param {string|null|undefined} categoryValue
 * @param {string|null|undefined} sku
 * @param {string[]} eligibleKeys - from settings (default salmon + sausage)
 * @returns {boolean}
 */
export function skuNeedsPackingTempCheck(categoryValue, sku, eligibleKeys = PACKING_TEMP_CATEGORY_KEYS) {
    const key = resolvePackingTempCategoryKey(categoryValue, sku);
    if (!key) return false;
    const allowed = Array.isArray(eligibleKeys) && eligibleKeys.length > 0
        ? eligibleKeys.map((k) => String(k).toLowerCase())
        : [...PACKING_TEMP_CATEGORY_KEYS];
    return allowed.includes(key);
}

/**
 * @param {{ sku?: string, code?: string, category?: string, Category?: string }|null|undefined} product
 * @param {(sku: string) => string|null|undefined} [getCategory]
 * @param {string[]} [eligibleKeys]
 * @returns {boolean}
 */
export function productNeedsPackingTempCheck(product, getCategory, eligibleKeys) {
    if (!product) return false;
    const sku = product.sku || product.code || product.Code || '';
    let category = product.category || product.Category || '';
    if ((!category || category === 'Other') && sku && typeof getCategory === 'function') {
        try {
            category = getCategory(sku) || category;
        } catch {
            /* ignore */
        }
    }
    return skuNeedsPackingTempCheck(category, sku, eligibleKeys);
}
